import React from 'react'
import PropTypes from 'prop-types'
import {InfoIcon} from '../../icons'
import CurrentPrice from '../../display-price/current-price'
import Discount from '../../display-price/discount'
import ListPrice from '../../display-price/list-price'
import OmnibusPrice from '../../display-price/omnibus-price'
import VatLabel from '../../vat-label'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'

import {Flex, useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {remCalc} from '../../../theme/_utils'
import InfoPopover from '../../popovers/info-popover'
import {useIntl} from 'react-intl'

const PriceSection = ({priceData, currency, notShoppableCountry, isPlpCompare = false}) => {
    const isVATFlag = useBooleanCustomPreference('showVATFlag')

    const {listPrice, currentPrice, isASet, isMaster, isOnSale, isRange} = priceData || {}
    const isMobile = useBreakpointValue({base: true, sm: true, md: false})

    const intl = useIntl()
    if (notShoppableCountry || !priceData) {
        return null
    }

    const isMobileCompare = Boolean(isPlpCompare && isMobile)

    const currentPriceIsRange = isASet ? true : Boolean(isMaster && isRange)
    const listPriceIsRange = Boolean(isMaster && isRange)
    const showOriginalPrice = isOnSale && Boolean(listPrice)
    const discountPrice =
        showOriginalPrice && listPrice > 0 ? (listPrice - currentPrice) / listPrice : undefined

    return (
        <Flex
            flexDir="column"
            gap="xxxsmall-1.base"
            width="auto"
            alignItems={isMobileCompare ? 'center' : 'flex-start'}
        >
            <Flex
                alignItems="center"
                gap="xxxsmall-1.base"
                width="auto"
                wrap="wrap"
                justifyContent={isMobileCompare ? 'center' : 'flex-start'}
            >
                <Flex alignItems="center" gap="xxxsmall-1.base" wrap="nowrap">
                    <CurrentPrice
                        price={currentPrice}
                        as="p"
                        currency={currency}
                        isRange={currentPriceIsRange}
                    />
                    {showOriginalPrice && (
                        <ListPrice
                            price={listPrice}
                            currency={currency}
                            isRange={listPriceIsRange}
                            showSuggestesLabel={false}
                        />
                    )}
                </Flex>
                {discountPrice !== undefined && <Discount price={discountPrice} />}
                {isOnSale && !isPlpCompare ? (
                    <InfoPopover
                        trigger={
                            <Flex flexDir="column" alignItems="flex-start">
                                <InfoIcon
                                    aspectRatio="1/1"
                                    width={remCalc(16)}
                                    color="foreground.neutral.subtle"
                                />
                            </Flex>
                        }
                        variant="top-left-no-y-offset"
                        openTooltipOn="hover"
                        title={intl.formatMessage({
                            id: 'price_card.info.popover.title',
                            defaultMessage: 'PriceCard Popover Title',
                        })}
                        description={intl.formatMessage({
                            id: 'price_card.info.popover.description',
                            defaultMessage: 'PriceCard Popover Description',
                        })}
                    />
                ) : (
                    <></>
                )}
            </Flex>

            <Flex
                width="auto"
                alignItems="center"
                justifyContent={isMobileCompare ? 'center' : 'flex-start'}
                gap="xxxsmall-1.base"
                wrap="wrap"
                minH={remCalc(20)}
            >
                {priceData.showOmnibusPriceDiscount && (
                    <OmnibusPrice
                        priceData={priceData}
                        currency={currency}
                        omnibusExpandedView={false}
                        isPlpCompareMobile={isMobileCompare}
                    />
                )}
                {isVATFlag && (
                    <VatLabel
                        price={priceData?.currentPrice}
                        vatRate={priceData.taxRate}
                        variant="tile"
                    />
                )}
            </Flex>
        </Flex>
    )
}

PriceSection.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number,
        isOnSale: PropTypes.bool,
        listPrice: PropTypes.number,
        isASet: PropTypes.bool,
        isMaster: PropTypes.bool,
        isRange: PropTypes.bool,
        showOmnibusPriceDiscount: PropTypes.bool,
        taxRate: PropTypes.number,
    }),
    isPlpCompare: PropTypes.bool,
    currency: PropTypes.string,
    notShoppableCountry: PropTypes.bool,
}

export default PriceSection
